<template>
    <div class="card mb-3 p-3 calendar-stats-v2">
        <div class="d-flex justify-content-between align-items-center mb-2">
            <div>
                <div class="stats-title">Riepilogo calendario</div>
                <div class="text-muted small">
                    Sintesi operativa delle pianificazioni caricate nel periodo selezionato.
                </div>
            </div>

            <div class="stats-actions">
                <button
                    type="button"
                    class="btn btn-sm btn-warning"
                    :disabled="diagnosticsLoading"
                    @click="$emit('diagnostics')"
                >
                    {{ diagnosticsLoading ? 'Controllo...' : 'Controlla calendario' }}
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="$emit('close')">
                    Chiudi
                </button>
            </div>
        </div>

        <div class="row">
            <div class="col-md-3 mb-2">
                <div class="kpi-box">
                    <div class="kpi-label">Giornate</div>
                    <div class="kpi-value">{{ totalDays }}</div>
                </div>
            </div>

            <div class="col-md-3 mb-2">
                <div class="kpi-box">
                    <div class="kpi-label">Slot caricati</div>
                    <div class="kpi-value">{{ totalSlots }}</div>
                </div>
            </div>

            <div class="col-md-3 mb-2">
                <div class="kpi-box">
                    <div class="kpi-label">Ore pianificate</div>
                    <div class="kpi-value">{{ totalHours }}</div>
                </div>
            </div>

            <div class="col-md-3 mb-2">
                <div class="kpi-box">
                    <div class="kpi-label">Moduli coinvolti</div>
                    <div class="kpi-value">{{ totalModules }}</div>
                </div>
            </div>
        </div>

        <div v-if="moduleRows.length" class="table-responsive mt-2">
            <table class="table table-sm table-bordered mb-0">
                <thead>
                <tr>
                    <th>Modulo</th>
                    <th class="text-right">Ore nel periodo</th>
                    <th class="text-right">Slot</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="row in moduleRows" :key="row.id">
                    <td>{{ row.nome }}</td>
                    <td class="text-right">{{ row.hours }}</td>
                    <td class="text-right">{{ row.slots }}</td>
                </tr>
                </tbody>
            </table>
        </div>

        <div v-if="diagnostics" class="diagnostics-panel mt-3">
            <div class="diagnostics-title">Controllo calendario</div>

            <div class="row">
                <div class="col-md-4 mb-2">
                    <div class="diagnostics-kpi" :class="{ danger: diagnosticsSummary.exact_duplicates > 0 }">
                        <span>Duplicati DB</span>
                        <strong>{{ diagnosticsSummary.exact_duplicates }}</strong>
                    </div>
                </div>
                <div class="col-md-4 mb-2">
                    <div class="diagnostics-kpi" :class="{ danger: diagnosticsSummary.cell_collisions > 0 }">
                        <span>Celle sovrapposte</span>
                        <strong>{{ diagnosticsSummary.cell_collisions }}</strong>
                    </div>
                </div>
                <div class="col-md-4 mb-2">
                    <div class="diagnostics-kpi" :class="{ danger: diagnosticsSummary.tutor_overlaps > 0 }">
                        <span>Tutor sovrapposti</span>
                        <strong>{{ diagnosticsSummary.tutor_overlaps }}</strong>
                    </div>
                </div>
            </div>

            <div v-if="diagnosticsSummary.exact_duplicates > 0" class="diagnostics-section">
                <div class="diagnostics-subtitle">Duplicati esatti</div>
                <div
                    v-for="item in diagnostics.exact_duplicates"
                    :key="'dup_' + item.giornata_ids.join('_')"
                    class="diagnostics-row"
                >
                    <strong>{{ formatDate(item.date) }} {{ item.start }}-{{ item.end }}</strong>
                    <span>ID giornate: {{ item.giornata_ids.join(', ') }}</span>
                </div>
            </div>

            <div v-if="diagnosticsSummary.cell_collisions > 0" class="diagnostics-section">
                <div class="diagnostics-subtitle">Celle che Calendar V2 può mascherare</div>
                <div
                    v-for="item in diagnostics.cell_collisions"
                    :key="'cell_' + item.key"
                    class="diagnostics-row"
                >
                    <strong>{{ formatDate(item.date) }} {{ item.start }} aula {{ item.aula_id || '-' }}</strong>
                    <span>{{ item.items.length }} righe nello stesso slot: {{ getDiagnosticItemIds(item.items) }}</span>
                </div>
            </div>

            <div v-if="diagnosticsSummary.tutor_overlaps > 0" class="diagnostics-section">
                <div class="diagnostics-subtitle">Sovrapposizioni tutor</div>
                <div
                    v-for="(item, index) in diagnostics.tutor_overlaps"
                    :key="'tutor_' + index"
                    class="diagnostics-row"
                >
                    <strong>{{ formatDate(item.date) }} tutor #{{ item.tutor_id }}</strong>
                    <span>
                        Locale {{ item.local.tutor_start }}-{{ item.local.tutor_end }} ID {{ item.local.giornata_id }}
                        - corso {{ item.conflict.corso_id }} ID {{ item.conflict.giornata_id }}
                        {{ item.conflict.tutor_start }}-{{ item.conflict.tutor_end }}
                    </span>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
export default {
    props: {
        days: {
            type: Array,
            default: () => []
        },
        slots: {
            type: Array,
            default: () => []
        },
        meta: {
            type: Object,
            default: () => ({})
        },
        diagnostics: {
            type: Object,
            default: null
        },
        diagnosticsLoading: {
            type: Boolean,
            default: false
        }
    },

    computed: {
        totalDays() {
            return (this.days || []).length
        },

        totalSlots() {
            return (this.slots || []).length
        },

        totalHours() {
            const total = (this.slots || []).reduce((carry, slot) => {
                return carry + Number(slot.duration_hours || slot.durata || 1)
            }, 0)

            return Number(total.toFixed(2))
        },

        totalModules() {
            const ids = (this.slots || [])
                .map((slot) => slot.modulo_id)
                .filter((id) => !!id)

            return [...new Set(ids)].length
        },

        moduleRows() {
            const rows = {}

            ;(this.slots || []).forEach((slot) => {
                const id = slot.modulo_id || 'unknown'

                if (!rows[id]) {
                    rows[id] = {
                        id,
                        nome: slot.modulo_nome || slot.modulo_short || 'Modulo non specificato',
                        hours: 0,
                        slots: 0
                    }
                }

                rows[id].hours += Number(slot.duration_hours || slot.durata || 1)
                rows[id].slots += 1
            })

            return Object.values(rows)
                .map((row) => ({
                    ...row,
                    hours: Number(row.hours.toFixed(2))
                }))
                .sort((a, b) => String(a.nome).localeCompare(String(b.nome)))
        },

        diagnosticsSummary() {
            return this.diagnostics && this.diagnostics.summary
                ? this.diagnostics.summary
                : {
                    exact_duplicates: 0,
                    cell_collisions: 0,
                    tutor_overlaps: 0
                }
        }
    },

    methods: {
        formatDate(date) {
            if (!date) return ''

            const parts = String(date).split('-')

            if (parts.length !== 3) {
                return date
            }

            return parts[2] + '/' + parts[1] + '/' + parts[0]
        },

        getDiagnosticItemIds(items) {
            return (items || [])
                .map(function (item) {
                    return item.giornata_id
                })
                .join(', ')
        }
    }
}
</script>

<style scoped>
.calendar-stats-v2 {
    background: #ffffff;
}

.stats-title {
    font-size: 16px;
    font-weight: 700;
}

.stats-actions {
    display: flex;
    gap: 6px;
    align-items: center;
}

.kpi-box {
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 10px;
    background: #f8f9fa;
}

.kpi-label {
    font-size: 11px;
    text-transform: uppercase;
    color: #6c757d;
}

.kpi-value {
    font-size: 22px;
    font-weight: 700;
    line-height: 1.2;
}

.diagnostics-panel {
    border-top: 1px solid #e5e7eb;
    padding-top: 10px;
}

.diagnostics-title {
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 8px;
}

.diagnostics-kpi {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    padding: 8px 10px;
    background: #f9fafb;
}

.diagnostics-kpi.danger {
    border-color: #f59e0b;
    background: #fffbeb;
}

.diagnostics-section {
    margin-top: 8px;
}

.diagnostics-subtitle {
    font-weight: 700;
    font-size: 12px;
    margin-bottom: 4px;
}

.diagnostics-row {
    display: flex;
    flex-direction: column;
    gap: 2px;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    padding: 6px 8px;
    margin-bottom: 4px;
    font-size: 12px;
    background: #ffffff;
}
</style>
